import React from 'react';
import { arrayOf, bool, number, shape, string } from 'prop-types';
import { Box, Divider, Typography } from '@mui/material';
import { BarChart, useAxisTooltip } from '@mui/x-charts-pro';
import { useTheme } from '@mui/material/styles';

import text from '../../../../../../localization/Application/Overview/charts';
import emptyStateIcon from '../../../../../../assets/icons/overview-chart.svg';
import apiRequests7DaysBlur from '../../../../../../assets/images/overviewGraphs/api-requests-7.png';
import apiRequests30DaysBlur from '../../../../../../assets/images/overviewGraphs/api-requests-30.png';

import { EmptyStateContainer, EmptyStateImage } from '../../../../../../global-styles/components';
import { UpgradeOverlay } from '../../../../../../components';
import CustomLegend from '../CustomLegend';
import { ColorBox, FormattedTypography, TooltipWrapper } from '../styles';

import chartUtils from '../util/chartUtils';

const staticText = text.apiRequestsOverTimeChart;

function capitalizeFirstLetterOnly(str) {
  const formattedString = str.toLowerCase().replace(/_/g, ' ');
  return formattedString.replace(/[a-z]/i, match => match.toUpperCase());
}

const getCustomTooltipComponent = (dataset, theme) => () => {
  const tooltipData = useAxisTooltip();

  if (!chartUtils.isTooltipDataValid(tooltipData)) {
    return null;
  }

  const datasetIndex = tooltipData.identifier.x.index;

  const { errorCounts } = dataset[datasetIndex];
  const totalRequests = dataset[datasetIndex].success
    + dataset[datasetIndex].error;
  return (
    <TooltipWrapper>
      <FormattedTypography
        fontFamily={theme.typography.bold.fontFamily}
        marginBottom={0.5}
      >
        {staticText.yAxisLabel}
      </FormattedTypography>
      <FormattedTypography marginBottom={2}>{tooltipData.axisFormattedValue}</FormattedTypography>
      <Box display="flex" alignItems="center" marginBottom={2}>
        <ColorBox color="green" />
        <FormattedTypography
          fontFamily={theme.typography.bold.fontFamily}
          flexGrow={1}
          marginRight={2}
        >
          {tooltipData.seriesItems[0].formattedLabel}
        </FormattedTypography>
        <FormattedTypography>{dataset[datasetIndex].success}</FormattedTypography>
      </Box>
      <Box display="flex" alignItems="center" marginBottom={1}>
        <ColorBox color="purple" />
        <FormattedTypography
          fontFamily={theme.typography.bold.fontFamily}
          flexGrow={1}
        >
          {tooltipData.seriesItems[1].formattedLabel}
        </FormattedTypography>
        <FormattedTypography>{dataset[datasetIndex].error}</FormattedTypography>
      </Box>
      {errorCounts.map(({ code, count }) => (
        <Box key={code} display="flex" alignItems="center" marginBottom={1}>
          <FormattedTypography
            marginLeft={3}
            marginRight={2}
            flexGrow={1}
          >
            {capitalizeFirstLetterOnly(code)}
          </FormattedTypography>
          <FormattedTypography>{count}</FormattedTypography>
        </Box>
        ))}
      <Divider sx={{ marginTop: 2, marginBottom: 2 }} />
      <Box display="flex" alignItems="center">
        <FormattedTypography
          fontFamily={theme.typography.bold.fontFamily}
          flexGrow={1}
          marginLeft={3}
          marginRight={1}
        >
          {staticText.total}
        </FormattedTypography>
        <FormattedTypography>{totalRequests}</FormattedTypography>
      </Box>
    </TooltipWrapper>
  );
};

const APIRequestsOverTimeChart = ({
  dataset, maxDays, showOnboarding, timeInterval,
}) => {
  const theme = useTheme();

  const legendItems = [
    {
      color: 'green',
      label: staticText.successLabel,
    },
    {
      color: 'purple',
      label: staticText.errorLabel,
    },
  ];

  const emptyState = (
    <EmptyStateContainer paperBackground condensed sx={{ flexGrow: 1 }}>
      <EmptyStateImage
        src={emptyStateIcon}
        alt=""
        sx={{ mb: theme.spacing(3) }}
      />
      <Typography variant="bold" marginBottom={1}>
        {staticText.emptyStateTitle}
      </Typography>
      <Typography variant="body2" marginBottom={3}>
        {showOnboarding
            ? staticText.emptyStateSubtitleWhileOnboarding
            : staticText.emptyStateSubtitleAfterOnboarding
          }
      </Typography>
    </EmptyStateContainer>
  );

  if (timeInterval > maxDays) {
    const imgSrc = timeInterval === 30 ? apiRequests30DaysBlur : apiRequests7DaysBlur;
    return (
      <Box sx={{ display: 'grid' }}>
        <UpgradeOverlay staticText={staticText.upgradeContent} />
        <img
          src={imgSrc}
          alt={`Bar graph showing API requests over ${timeInterval} days`}
          style={{
            width: '100%',
            gridRow: 1,
            gridColumn: 1,
          }}
        />
      </Box>
    );
  }

  if (!dataset.length) {
    return emptyState;
  }

  return (
    <Box
      display="flex"
      flexDirection="column"
    >
      <BarChart
        dataset={dataset}
        xAxis={[{
          scaleType: 'band',
          dataKey: 'date',
          label: staticText.xAxisLabel(timeInterval),
          valueFormatter: chartUtils.commonChartOptions.dateTickFormatter(timeInterval),
          // todo: only add this if screen width is less than a certain size
          ...(timeInterval === 1 ? { tickInterval: (val, i) => i % 2 === 0 } : {}),
        }]}
        yAxis={[{ tickNumber: 5, label: staticText.yAxisLabel }]}
        series={[
          {
            dataKey: 'success',
            stack: 'total',
            color: theme.palette.charts.green.fill,
            stackOrder: 'reverse',
            highlightScope: {
              highlighted: 'none',
              faded: 'global',
            },
            label: staticText.successLabel,
          },
          {
            dataKey: 'error',
            stack: 'total',
            color: theme.palette.charts.purple.fill,
            highlightScope: {
              highlighted: 'none',
              faded: 'global',
            },
            label: staticText.errorLabel,
          },
        ]}
        borderRadius={3}
        axisHighlight={{ x: 'none' }}
        grid={{ horizontal: true }}
        slotProps={chartUtils.commonChartOptions.slotProps}
        slots={{
          axisContent: getCustomTooltipComponent(dataset, theme),
        }}
        margin={{
          right: 0, left: 88, top: 24,
        }}
        height={330}
        sx={{
          '& .MuiBarElement-series-auto-generated-id-0': {
            stroke: theme.palette.charts.green.stroke,
          },
          '& .MuiBarElement-series-auto-generated-id-1': {
            stroke: theme.palette.charts.purple.stroke,
          },
        }}
      />
      <CustomLegend legendItems={legendItems} />
    </Box>
  );
};

export default APIRequestsOverTimeChart;

APIRequestsOverTimeChart.propTypes = {
  dataset: arrayOf(shape({
    date: string.isRequired,
    success: number.isRequired,
    error: number.isRequired,
    errorCounts: arrayOf(shape({
      code: string.isRequired,
      count: number.isRequired,
    })).isRequired,
  })).isRequired,
  maxDays: number.isRequired,
  showOnboarding: bool.isRequired,
  timeInterval: number.isRequired,
};

